import * as React from "react";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Checkbox from "@mui/material/Checkbox";
import { Objective, Topic } from "../../interfaces/topicInterfaces";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { selectObjectiveIDs } from "../../selectors/selectors";
import { ObjectiveInterface } from "../../interfaces/examInterfaces";
import { addObjective, removeObjective } from "./Functions/FunctionsChangeObjectives";
import { SelectObjectiveDialogInfoBox } from "./Styles";

export const SelectingObjectivesTable = ({ topic, index }: { topic: Topic; index: number }) => {
  const [selectedObjectives, setSelectedObjectives] = useState<number[]>([]);
  const [isTopicSelected, setIsTopicSelected] = useState(false);
  const labelId = `enhanced-table-checkbox-${index}`;
  const selectedObjectiveIds = useSelector(selectObjectiveIDs);

  //Gets the data of selected objectives from the reducer and sets it
  useEffect(() => {
    const filteredObjectiveIds = topic.objectives
      .filter((objective) => selectedObjectiveIds.includes(objective.id))
      .map((objective) => objective.id);

    const numericObjectiveIds = filteredObjectiveIds.map((id) => +id);
    setSelectedObjectives(numericObjectiveIds);
  }, [selectedObjectiveIds]);

  //Sets the topic checkbox to true or false depending on the amount of objectives selected
  useEffect(() => {
    if (selectedObjectives.length === topic.objectives.length) {
      setIsTopicSelected(true);
    } else {
      setIsTopicSelected(false);
    }
  }, [selectedObjectives]);

  const handleClickTopic = () => {
    let newSelectedObjectives = [...selectedObjectives];
    const allSelected = topic.objectives.every((objective) => newSelectedObjectives.includes(+objective.id));

    if (allSelected) {
      newSelectedObjectives = newSelectedObjectives.filter(
        (id) => !topic.objectives.some((objective) => +objective.id === id)
      );
      selectedObjectives.forEach((objective) => {
        removeObjective(objective);
      });
    } else {
      topic.objectives.forEach((objective, index) => {
        const id = +objective.id;
        if (!newSelectedObjectives.includes(id)) {
          newSelectedObjectives.push(id);
          const newObjective = createObjective({ objective, topic: topic.topic, index });
          addObjective(newObjective);
        }
      });
    }
    setSelectedObjectives(newSelectedObjectives);
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Paper sx={{ width: "100%", mb: 2 }}>
        <TableContainer>
          <Table aria-labelledby="tableTitle">
            <TableBody>
              <TableRow
                hover
                onClick={() => handleClickTopic()}
                role="checkbox"
                aria-checked={isTopicSelected}
                tabIndex={-1}
                key={topic.id}
                selected={isTopicSelected}
                sx={{ cursor: "pointer" }}
              >
                <TableCell padding="checkbox" sx={{ position: "relative" }}>
                  <Checkbox
                    color="primary"
                    checked={isTopicSelected}
                    inputProps={{
                      "aria-labelledby": labelId,
                    }}
                  />
                </TableCell>
                <TableCell component="th" id={labelId} scope="row" padding="none">
                  {topic.topic}
                </TableCell>
              </TableRow>

              {topic.objectives.map((objective, index) => (
                <ObjectiveRow
                  key={index}
                  index={index}
                  objective={objective}
                  selectedObjectives={selectedObjectives}
                  labelId={labelId}
                  setSelectedObjective={setSelectedObjectives}
                  addObjective={addObjective}
                  removeObjective={removeObjective}
                  topic={topic.topic}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

const ObjectiveRow = ({
  index,
  objective,
  selectedObjectives,
  labelId,
  topic,
  setSelectedObjective,
  addObjective,
  removeObjective,
}: {
  index: number;
  objective: Objective;
  selectedObjectives: number[];
  labelId: string;
  topic: string;
  setSelectedObjective: (newSelected: number[]) => void;
  addObjective: (objective: ObjectiveInterface) => void;
  removeObjective: (objective: number) => void;
}) => {
  const isObjectiveSelected = selectedObjectives.includes(+objective.id);

  const handleClickObjective = (event: React.MouseEvent<unknown>, id: number) => {
    const selectedIndex = selectedObjectives.indexOf(id);
    let newSelected = setSelectedRow(selectedIndex, selectedObjectives, id);
    if (selectedObjectives.length > newSelected.length) {
      removeObjective(id);
    }
    if (selectedObjectives.length < newSelected.length) {
      const newObjective = createObjective({ objective, topic, index });
      addObjective(newObjective);
    }
    setSelectedObjective(newSelected);
  };

  return (
    <>
      <TableRow
        hover
        onClick={(event) => handleClickObjective(event, +objective.id)}
        role="checkbox"
        aria-checked={isObjectiveSelected}
        tabIndex={-1}
        key={objective.id}
        selected={isObjectiveSelected}
      >
        <TableCell padding="checkbox" sx={{ position: "relative" }}>
          <Checkbox
            color="primary"
            checked={isObjectiveSelected}
            inputProps={{
              "aria-labelledby": labelId,
            }}
          />
        </TableCell>
        <TableCell>
          {index + 1}. {objective.objective}
          {objective.info && (
            <SelectObjectiveDialogInfoBox>
              {objective.info}
            </SelectObjectiveDialogInfoBox>
          )}
        </TableCell>
      </TableRow>
    </>
  );
};

function setSelectedRow(selectedIndex: number, selected: number[], id: number) {
  let newSelected: number[] = [];
  if (selectedIndex === -1) {
    newSelected = newSelected.concat(selected, id);
  } else if (selectedIndex === 0) {
    newSelected = newSelected.concat(selected.slice(1));
  } else if (selectedIndex === selected.length - 1) {
    newSelected = newSelected.concat(selected.slice(0, -1));
  } else if (selectedIndex > 0) {
    newSelected = newSelected.concat(selected.slice(0, selectedIndex), selected.slice(selectedIndex + 1));
  }

  return newSelected;
}

function createObjective({ topic, index, objective }: { topic: string; index: number; objective: Objective }) {
  const id = objective.id.toString();
  const objectiveData = objective.objective;
  const newObjective: ObjectiveInterface = {
    id: id,
    topic: topic,
    objectiveIndex: index + 1,
    objective: objectiveData,
    exercises: {},
  };
  return newObjective;
}
